import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO Meta tags for different pages
const pageMeta = {
  '/': {
    title: 'MarkeeMix - AI Workspace for Teachers, Creators & Marketers | 75+ Tools',
    description: 'MarkeeMix offers 75+ AI tools built for teachers, creators, and marketers for SEO, content marketing, social media, PDF editing, and advertising. Start your free trial today!',
    keywords: 'digital marketing, SEO tools, content marketing, social media tools, MarkeeMix'
  },
  '/tools': {
    title: 'All Marketing Tools | MarkeeMix - 75+ Free & Pro Tools',
    description: 'Explore 75+ AI tools including SEO analyzers, content writers, PDF editors, social media schedulers, and more. Free and Pro tools available.',
    keywords: 'marketing tools, SEO tools, PDF tools, content tools, free marketing tools'
  },
  '/tools/seo': {
    title: 'SEO Tools - Keyword Research, Site Audit, Backlinks | MarkeeMix',
    description: 'Powerful SEO tools for keyword research, site audit, backlink analysis, rank tracking, and competitor analysis. Boost your search rankings.',
    keywords: 'SEO tools, keyword research, site audit, backlink analyzer, rank tracking'
  },
  '/tools/seo/dashboard': {
    title: 'SEO Dashboard - Track Your Website Performance | MarkeeMix',
    description: 'Monitor your website SEO performance with real-time analytics, keyword rankings, traffic data, and actionable insights.',
    keywords: 'SEO dashboard, SEO analytics, keyword tracking, website performance'
  },
  '/tools/seo/technical': {
    title: 'Technical SEO Tools - 18+ Advanced Analyzers | MarkeeMix',
    description: 'Technical SEO tools including robots.txt tester, sitemap generator, mobile-friendly test, schema generator, and more.',
    keywords: 'technical SEO, robots.txt tester, sitemap generator, schema generator, AMP validator'
  },
  '/tools/content': {
    title: 'Content Marketing Tools - AI Writer, Plagiarism Checker | MarkeeMix',
    description: 'AI-powered content tools for writing, plagiarism detection, readability analysis, and topic research. Create better content faster.',
    keywords: 'content marketing, AI writer, plagiarism checker, readability analyzer, content tools'
  },
  '/tools/design/pdf': {
    title: 'PDF Tools - Merge, Split, Compress, Edit PDF Online | MarkeeMix',
    description: '40+ PDF tools to merge, split, compress, edit, convert, and secure your PDF files. Free online PDF editor with advanced features.',
    keywords: 'PDF tools, merge PDF, split PDF, compress PDF, PDF editor, convert PDF'
  },
  '/tools/social-media': {
    title: 'Social Media Tools - Scheduler, Analytics, Hashtags | MarkeeMix',
    description: 'Social media management tools for scheduling posts, analyzing engagement, generating hashtags, and growing your audience.',
    keywords: 'social media tools, post scheduler, hashtag generator, social analytics'
  },
  '/tools/design': {
    title: 'Design Tools - Logo Maker, Banner Creator | MarkeeMix',
    description: 'Create stunning designs with AI-powered logo maker, banner creator, thumbnail designer, and color palette generator.',
    keywords: 'design tools, logo maker, banner creator, thumbnail designer, color palette'
  },
  '/tools/google-ads': {
    title: 'Google Ads Tools - Keyword Planner, Ad Copy Generator | MarkeeMix',
    description: 'Optimize your Google Ads campaigns with keyword planner, AI ad copy generator, bid optimizer, and competitor research tools.',
    keywords: 'Google Ads tools, keyword planner, ad copy generator, PPC tools'
  },
  '/tools/facebook-ads': {
    title: 'Facebook Ads Tools - Audience Builder, Creative Studio | MarkeeMix',
    description: 'Master Facebook and Instagram advertising with audience builder, creative studio, A/B testing, and performance analytics.',
    keywords: 'Facebook Ads tools, audience builder, Meta ads, Instagram ads'
  },
  '/pricing': {
    title: 'Pricing - MarkeeMix | Free · Starter ₹199 · Pro ₹499 · Business ₹1499',
    description: 'India-first pricing for MarkeeMix. Start free, pay ₹199/month as a solo teacher or creator, ₹499 for Pro, ₹1499 for Business teams. All plans include AI-powered tools.',
    keywords: 'MarkeeMix pricing India, teacher plan, creator plan, ₹199 marketing tools, INR pricing'
  },
  '/blogs': {
    title: 'Marketing Blog - Tips, Guides & Insights | MarkeeMix',
    description: 'Learn digital marketing with our expert blog posts covering SEO, content marketing, social media, advertising, and more.',
    keywords: 'marketing blog, SEO tips, content marketing guides, digital marketing insights'
  },
  '/task-manager': {
    title: 'Task Manager - Project Management for Marketing Teams | MarkeeMix',
    description: 'Manage your marketing projects with our powerful task manager. Track clients, projects, tasks, and team productivity.',
    keywords: 'task manager, project management, marketing workflow, team collaboration'
  },
  '/about': {
    title: 'About MarkeeMix - Our Story & Mission',
    description: 'Learn about MarkeeMix, the AI workspace for teachers, creators & marketers helping businesses grow with 75+ powerful tools.',
    keywords: 'about MarkeeMix, digital marketing platform, marketing tools company'
  },
  '/contact': {
    title: 'Contact Us - Get in Touch | MarkeeMix',
    description: 'Have questions? Contact the MarkeeMix team. We\'re here to help you succeed in digital marketing.',
    keywords: 'contact MarkeeMix, support, help, customer service'
  }
};

// Hook to update meta tags
export function useSEO() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const meta = pageMeta[path] || pageMeta['/'];

    // Update title
    document.title = meta.title;

    // Update meta description
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', meta.description);
    }

    // Update meta keywords
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.setAttribute('content', meta.keywords);
    }

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', meta.title);
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', meta.description);
    }

    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', meta.title);
    }

    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', meta.description);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://markeemix.com${path}`);
    }

  }, [location.pathname]);
}

// SEO Component for page-level customization
export function SEOHead({ title, description, keywords }) {
  useEffect(() => {
    if (title) document.title = title;
    
    if (description) {
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta) descMeta.setAttribute('content', description);
    }
    
    if (keywords) {
      const keyMeta = document.querySelector('meta[name="keywords"]');
      if (keyMeta) keyMeta.setAttribute('content', keywords);
    }
  }, [title, description, keywords]);

  return null;
}

export default useSEO;
